"use client";

import React, { useState } from "react";
import { useTranslations } from "next-intl";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "@/i18n/navigation";
import { Button } from "@/components/spendplatform/Button";
import { CONTACT_EMAIL } from "@/components/spendplatform/constants";
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from "@/components/spendplatform/icons";

export default function Page() {
  const { login, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useTranslations();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await login(formData.email, formData.password, formData.rememberMe);
    } catch (error: any) {
      setError(error?.message || "An error occurred during sign in. Please try again.");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError(null);
  };

  return (
    <>
      <div
        className="sp-eyebrow"
        style={{ marginBottom: "var(--space-3)" }}
      >
        {t("authentication.signInPage.eyebrow")}
      </div>
      <h1
        className="sp-h1"
        style={{ marginBottom: "var(--space-3)", lineHeight: "var(--lh-tight)" }}
      >
        {t("authentication.signInPage.title")}
      </h1>
      <p
        className="sp-body"
        style={{ marginBottom: "var(--space-8)", textWrap: "pretty" }}
      >
        {t("authentication.signInPage.intro")}
      </p>

      {error && (
        <div
          role="alert"
          className="sp-alert"
          style={{ marginBottom: "var(--space-5)" }}
        >
          <AlertCircleIcon
            size={17}
            className="mt-px shrink-0"
            style={{ color: "var(--sroi-500)" }}
          />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div
          className="flex flex-col"
          style={{ gap: "var(--space-4)" }}
        >
          <label className="sp-field">
            <span className="sp-label">{t("authentication.emailAddress")}</span>
            <input
              type="email"
              name="email"
              id="email"
              className="sp-input"
              value={formData.email}
              onChange={handleInputChange}
              required
              autoComplete="username"
              placeholder={t("authentication.signInPage.emailPlaceholder")}
            />
          </label>

          <label className="sp-field">
            <span
              className="flex items-center justify-between"
              style={{ gap: "var(--space-4)" }}
            >
              <span className="sp-label">{t("authentication.password")}</span>
              <Link
                href="/auth/forgot-password"
                style={{ fontSize: "var(--fs-sm)", fontWeight: "var(--fw-semibold)", color: "var(--color-primary)" }}
              >
                {t("authentication.forgotPassword")}?
              </Link>
            </span>
            <span className="relative block">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                id="password"
                className="sp-input sp-input--password"
                value={formData.password}
                onChange={handleInputChange}
                required
                autoComplete="current-password"
                placeholder={t("authentication.signInPage.passwordPlaceholder")}
              />
              <button
                type="button"
                className="sp-input-toggle"
                aria-label={t("authentication.signInPage.togglePassword")}
                onClick={() => setShowPassword((shown) => !shown)}
              >
                {showPassword ? <EyeIcon size={18} /> : <EyeOffIcon size={18} />}
              </button>
            </span>
          </label>

          <label
            className="inline-flex cursor-pointer items-center gap-[9px]"
            style={{ marginTop: 2 }}
          >
            <input
              type="checkbox"
              name="rememberMe"
              id="rememberMe"
              className="sp-checkbox"
              checked={formData.rememberMe}
              onChange={handleInputChange}
            />
            <span style={{ fontSize: "var(--fs-sm)", color: "var(--ink-700)" }}>{t("authentication.signInPage.rememberDevice")}</span>
          </label>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            full
            disabled={isLoading}
          >
            {isLoading ? t("authentication.signingIn") : t("authentication.signIn")}
          </Button>
        </div>
      </form>

      <div
        style={{ marginTop: "var(--space-8)", paddingTop: "var(--space-5)", borderTop: "1px solid var(--border-default)", fontSize: "var(--fs-sm)", color: "var(--text-muted)" }}
      >
        {t("authentication.signInPage.noEnvironment")}{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          style={{ fontWeight: "var(--fw-bold)", color: "var(--color-primary)" }}
        >
          {t("authentication.signInPage.requestIntroduction")}
        </a>
      </div>
    </>
  );
}
